import {
  GeoJson,
  GroundingConfigurationSchema,
  GroundingSchema,
  ReadGroundingsGroundingsGetRequest,
} from '@ais-ui/api-spec/grounding';
import React, { useCallback, useMemo } from 'react';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
} from '@ais-ui/ui-core';
import { capitalize, cn, formatVesselName, isNonEmpty } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { useShipSelection } from '../../../Map/contexts';
import {
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
  smartZoomTo,
} from '@ais-ui/react-openlayers';
import { PiDotsThreeVertical } from 'react-icons/pi';
import { AutoCompleteShipInfo } from '@ais-ui/websockets';
import { transform } from 'ol/proj';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Fill, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { DateRange } from 'react-day-picker';
import {
  useFetchAllGroundingConfigurationDefinitions,
  useGetGroundingsWithDateTimes,
  useShapeFetch,
} from '@ais-ui/api';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { EventGridItem } from '../../EventGridItem';
import OlGeometry from 'ol/geom/Geometry';
import { useTimeSince } from '../../../conversions/useTimeSince';

interface GroundingEventItemProps {
  event: GroundingSchema;
}

export interface CombinedGroundingEventAndConfiguration {
  event: GroundingSchema;
  configuration: GroundingConfigurationSchema;
}

const ShipEventItem = ({ event }: GroundingEventItemProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <EventGridItem
        label={messages.eventsItemShipNameLabel}
        value={formatVesselName(event.vesselName)}
      />
      <EventGridItem
        label={messages.eventsItemMmsiLabel}
        value={event.mmsi.toString()}
      />
      <EventGridItem
        label={messages.eventsItemImoLabel}
        value={event.imo?.toString()}
      />
      <EventGridItem
        label={messages.eventsItemCallsignLabel}
        value={event.callSign}
      />
    </div>
  );
};

const GroundingEventDetailsItem = ({
  event,
  configuration,
}: CombinedGroundingEventAndConfiguration) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="flex flex-col space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1 min-w-0">
          <p className="text-sm font-medium flex-none">
            {messages.eventsItemSeverityLabel}
          </p>
          <div className="flex items-center space-x-1.5">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                configuration.severity === 'note' && 'bg-blue-500',
                configuration.severity === 'warning' && 'bg-yellow-500',
                configuration.severity === 'alert' && 'bg-red-500',
              )}
            ></div>
            <p className="text-sm text-muted-foreground flex-grow truncate">
              {isNonEmpty(capitalize(configuration.severity as string))
                ? capitalize(configuration.severity as string)
                : 'Unknown'}
            </p>
          </div>
        </div>
        <EventGridItem
          label={messages.eventsItemDetectorLabel}
          value={configuration.name}
        />
      </div>
      <EventGridItem
        label={messages.eventsItemActivatedLabel}
        value={formatDateTimeWithTimeZone(event.groundingTime)}
      />
      <EventGridItem
        label={messages.eventsItemDeactivatedLabel}
        value={
          formatDateTimeWithTimeZone(event.releaseTime)
            ? formatDateTimeWithTimeZone(event.releaseTime)
            : messages.eventsItemDeactivatedFalseValue
        }
      />
    </div>
  );
};

const GroundingEventDropDownMenu = ({
  event,
  configuration,
  className,
}: {
  event: GroundingSchema;
  configuration: GroundingConfigurationSchema;
  className?: string;
}) => {
  const { messages } = useLocalization();
  const { setZoomToShip } = useShipSelection();

  const { map, displayProjection, dataProjection } = useOpenLayersContext();

  const { addLayer, doesLayerExist, zoomToLayer } = useLayers();

  const shapeFetch = useShapeFetch();

  const detectorShapeNameId = `Detector ${configuration.name}`;
  const onGotoEvent = useCallback(
    async (event: GroundingSchema) => {
      const transformedCenter = transform(
        event.groundingPoint.coordinates,
        dataProjection,
        displayProjection,
      );

      smartZoomTo({ coordinate: transformedCenter, map });

      // Create a feature for the grounding point
      const groundingFeature = new Feature({
        geometry: new Point(transformedCenter),
      });

      // Style the feature as a red dot
      groundingFeature.setStyle(
        new Style({
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color: 'red' }),
            stroke: new Stroke({ color: 'black', width: 1 }),
          }),
        }),
      );

      // Add the feature to a vector layer
      const vectorSource = new VectorSource({
        features: [groundingFeature],
      });

      const vectorLayer = new VectorLayer({
        source: vectorSource,
      });

      addLayer(
        vectorLayer,
        configuration.name + event.mmsi.toString(),
        true,
        true,
        true,
        {},
        '',
        false,
        true,
      );
    },
    [dataProjection, displayProjection, map, addLayer, configuration.name],
  );

  const onShowShape = useCallback(
    async (shapeId: string) => {
      if (doesLayerExist(detectorShapeNameId)) {
        return;
      }
      let scopedVectorLayer: VectorLayer<Feature<OlGeometry>> | undefined;
      let shape: GeoJson | undefined;
      if (shapeId) {
        shape = await shapeFetch(shapeId);
        scopedVectorLayer = shapeToVectorLayer(
          shape,
          displayProjection,
          dataProjection,
        );
      } else if (configuration.geometry) {
        shape = configuration.geometry;
        scopedVectorLayer = shapeToVectorLayer(
          shape,
          displayProjection,
          dataProjection,
        );
      } else {
        return;
      }

      addLayer(
        scopedVectorLayer,
        detectorShapeNameId,
        true,
        true,
        true,
        shape.properties,
        detectorShapeNameId,
        true,
      );
      zoomToLayer(scopedVectorLayer);
    },
    [
      doesLayerExist,
      detectorShapeNameId,
      shapeFetch,
      configuration.geometry,
      addLayer,
      zoomToLayer,
    ],
  );

  const onSelectShip = useCallback(async () => {
    const newSelectedShip: AutoCompleteShipInfo = {
      callSign: event.callSign ? event.callSign : null,
      imo: event.imo ? event.imo?.toString() : null,
      mmsi: event.mmsi ? event.mmsi.toString() : null,
      name: event.vesselName ? event.vesselName : null,
      type: 'TT_SHIP',
    };
    setZoomToShip(newSelectedShip);
  }, [event.callSign, event.imo, event.mmsi, event.vesselName, setZoomToShip]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button size="icon" variant="ghost" className={cn('size-8')}>
          <PiDotsThreeVertical className="size-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onSelectShip}>
          {messages.eventsActionsSelectShip}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onShowShape(configuration.shape as string)}
        >
          {messages.eventsActionsShowDetector}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onGotoEvent(event)}>
          {messages.eventsActionsShowEvent}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const GroundingEventItemContent = ({
  event,
  configuration,
}: CombinedGroundingEventAndConfiguration) => {
  return (
    <AccordionContent>
      <div className="grid gap-6 px-4 pt-4 [&_*]:min-w-0">
        <div className="grid gap-4">
          <div>
            <h3 className="font-medium text-base">Detected Ship</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <ShipEventItem event={event}></ShipEventItem>
        </div>
        <div className="grid gap-4">
          <div>
            <h3 className="font-medium text-base">Event</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <GroundingEventDetailsItem
            event={event}
            configuration={configuration}
          ></GroundingEventDetailsItem>
        </div>
      </div>
    </AccordionContent>
  );
};

export const useGetGroundingEventsAndConfigurations = (
  date: DateRange | undefined,
) => {
  const getGroundingParams: ReadGroundingsGroundingsGetRequest = {
    startDatetime: date?.from,
    endDatetime: date?.to,
  };
  const permissions = usePermissions();

  const shouldFetch = Boolean(
    permissions.grounding && date?.from !== undefined && date?.to !== undefined,
  );

  const { data: groundingData, isLoading: groundingIsLoading } =
    useGetGroundingsWithDateTimes(getGroundingParams, shouldFetch);

  const { data: groundingDetectors, isLoading: groundingConfIsLoading } =
    useFetchAllGroundingConfigurationDefinitions(shouldFetch);

  const actualGroundingIsLoading = shouldFetch ? groundingIsLoading : false;
  const actualConfigIsLoading = shouldFetch ? groundingConfIsLoading : false;

  const detectors = useMemo(() => {
    return groundingDetectors?.items || [];
  }, [groundingDetectors?.items]);

  const detectorMap = useMemo(() => {
    return new Map(
      detectors
        .filter((detectorConfig) => detectorConfig.id != null)
        .map((detectorConfig) => [detectorConfig.id, detectorConfig]),
    );
  }, [detectors]);

  const combinedConfAndEvents: CombinedGroundingEventAndConfiguration[] =
    useMemo(() => {
      if (!groundingData || !groundingDetectors) {
        return [];
      }

      const combined: CombinedGroundingEventAndConfiguration[] = [];
      groundingData.items.forEach((event) => {
        event.configurationIds?.forEach((configurationId) => {
          const configuration = detectorMap.get(configurationId);
          if (configuration) {
            combined.push({ event, configuration });
          }
        });
      });
      return combined;
    }, [groundingData, groundingDetectors, detectorMap]);

  return {
    groundingEventsAndConfigurations: combinedConfAndEvents,
    groundingIsLoading: actualGroundingIsLoading || actualConfigIsLoading,
    groundingConfIsLoading: actualConfigIsLoading,
  };
};

export const GroundingEventItem = ({
  event,
  configuration,
}: CombinedGroundingEventAndConfiguration) => {
  const timeAgo = useTimeSince(event.groundingTime);

  return (
    <AccordionItem
      key={event.id}
      value={event.uuid as string}
      className={cn('px-4')}
    >
      <div className="flex justify-between items-center">
        <div className="flex-grow">
          <AccordionTrigger className="w-full" hideChevron={true}>
            <div className="flex items-center w-full">
              <div className="ml-3 space-y-1">
                <p className="text-xs text-left font-medium leading-none group-hover:underline">
                  {configuration.name} - {event.vesselName}
                </p>
                <div className="flex items-center space-x-1.5">
                  <div
                    className={cn(
                      'w-2 h-2 rounded-full',
                      configuration.severity === 'note' && 'bg-blue-500',
                      configuration.severity === 'warning' && 'bg-yellow-500',
                      configuration.severity === 'alert' && 'bg-red-500',
                    )}
                  ></div>
                  <p className="text-sm text-left text-muted-foreground">
                    {timeAgo}
                  </p>
                </div>
              </div>
            </div>
          </AccordionTrigger>
        </div>
        <GroundingEventDropDownMenu
          event={event}
          configuration={configuration}
        ></GroundingEventDropDownMenu>
      </div>
      <GroundingEventItemContent
        event={event}
        configuration={configuration}
      ></GroundingEventItemContent>
    </AccordionItem>
  );
};
